import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type Job = components['schemas']['JobOut']
export type JobStatus = Job['status']
export type JobRetry = components['schemas']['JobRetryOut']

export const TERMINAL_JOB_STATUSES: ReadonlyArray<JobStatus> = ['succeeded', 'failed', 'cancelled']
export const ACTIVE_JOB_STATUSES: ReadonlyArray<JobStatus> = ['pending', 'running']

export function isTerminal(status: JobStatus): boolean {
  return TERMINAL_JOB_STATUSES.includes(status)
}

export interface UseJobsParams {
  category?: 'generation' | 'upload' | 'tool' | null
  status?: 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled' | null
  limit?: number
  offset?: number
}

export function useJobs({ category = null, status = null, limit = 50, offset = 0 }: UseJobsParams = {}) {
  return useQuery({
    queryKey: queryKeys.jobs.list({ category, status, limit, offset }),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/jobs', {
        params: {
          query: {
            ...(category ? { category } : {}),
            ...(status ? { status } : {}),
            limit,
            offset,
          },
        },
      })
      if (!data) throw new Error('jobs response empty')
      return data
    },
    staleTime: 5_000,
  })
}

/**
 * Live feed of the user's most recent jobs. Shared across the app (sidebar
 * badge, topbar bell, /jobs page, JobsWatcher toasts) via the common query
 * key — TanStack Query dedupes the polling so we issue one request every
 * ~3s no matter how many consumers subscribe.
 *
 * Returns the unfiltered latest page; consumers derive active / terminal
 * subsets client-side. The backend's status filter accepts a single value,
 * so filtering server-side would force two separate queries.
 */
export function useJobsFeed(limit = 50) {
  return useQuery({
    queryKey: queryKeys.jobs.feed(limit),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/jobs', {
        params: { query: { limit } },
      })
      if (!data) throw new Error('jobs feed response empty')
      return data
    },
    // 3s is brisk enough to catch completions quickly without hammering
    // the server — individual `useJob` polls at 1.5s for the foreground
    // panel, this feed runs in the background.
    refetchInterval: 3_000,
    // Keep polling even when window loses focus; we still want toasts and
    // badges to update when the user comes back.
    refetchIntervalInBackground: false,
    // Cheap to keep around — prior data shows up instantly when the user
    // navigates to /jobs, then refetches in the background.
    staleTime: 2_000,
  })
}

/**
 * "Continuar" — retry/resume a failed job (card #56). Spawns a NEW job that
 * reuses whatever the original produced and picks up where it stopped
 * (generation) or re-sends the upload with an anti-duplicate guard (upload).
 * ``force`` only matters for uploads (bypass the already-published guard).
 * Invalidates the jobs feed so the new job shows up immediately.
 */
export function useRetryJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ jobId, force = false }: { jobId: string; force?: boolean }): Promise<JobRetry> => {
      const { data } = await api.POST('/api/v1/jobs/{job_id}/retry', {
        params: { path: { job_id: jobId } },
        body: { force },
      })
      if (!data) throw new Error('no response from job retry')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * Cancelar um job da fila (docs/plan-job-queue.md, F2). `pending` sai
 * `cancelled` na hora; `running` ganha `cancel_requested_at` e o worker o
 * fecha logo depois. Sem toast de sucesso aqui: o `JobsWatcher` já avisa
 * "cancelado" quando o status muda.
 */
export function cancelJobOptions(qc: QueryClient) {
  return {
    mutationFn: async (jobId: string): Promise<Job> => {
      const { data } = await api.POST('/api/v1/jobs/{job_id}/cancel', {
        params: { path: { job_id: jobId } },
      })
      if (!data) throw new Error('no response from job cancel')
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.jobs.all }),
  }
}

export function useCancelJob() {
  const qc = useQueryClient()
  return useMutation(cancelJobOptions(qc))
}

export function useJob(jobId: string | null | undefined) {
  const safeId = jobId ?? ''
  return useQuery({
    queryKey: queryKeys.jobs.byId(safeId),
    queryFn: async (): Promise<Job> => {
      const { data } = await api.GET('/api/v1/jobs/{job_id}', {
        params: { path: { job_id: safeId } },
      })
      if (!data) throw new Error('job not found')
      return data.data
    },
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const s = query.state.data?.status
      return s === 'pending' || s === 'running' ? 1500 : false
    },
  })
}
